import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ImageIcon, Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import api from "../../lib/api";
import { IMAGES, mediaUrl, isVideo } from "./siteData";
import { useSeo } from "./useSeo";
import PageHero from "./PageHero";

const CATS = ["All", "Office", "Celebrations", "Events"];

export function useGallery() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/public/site/gallery").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return items;
}

function Lightbox({ items, index, onClose, onMove }) {
  const g = items[index];
  useEffect(() => {
    if (!g) return undefined;
    const key = (e) => { if (e.key === "ArrowRight") onMove(1); if (e.key === "ArrowLeft") onMove(-1); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onMove, g]);
  return (
    <Dialog open={!!g} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="site max-w-5xl !bg-[#0C1726] border-0 p-0 rounded-none [&>button]:hidden" data-testid="gallery-lightbox">
        {g && (
          <div className="relative">
            <DialogTitle className="sr-only">{g.caption || g.category}</DialogTitle>
            {isVideo(g.photo)
              ? <video key={g.id} src={mediaUrl(g.photo)} controls autoPlay playsInline className="w-full max-h-[78vh] bg-black" data-testid="gallery-lightbox-video" />
              : <img src={mediaUrl(g.photo)} alt={g.caption || g.category} className="w-full max-h-[78vh] object-contain bg-black" data-testid="gallery-lightbox-img" />}
            <div className="p-5 flex items-center justify-between text-white">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C4A66A]">{g.category}{g.taken_on ? ` · ${g.taken_on}` : ""}</div>
                {g.caption && <div className="font-serif text-xl mt-1">{g.caption}</div>}
              </div>
              <div className="text-xs text-white/50">{index + 1} / {items.length}</div>
            </div>
            <button onClick={() => onMove(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 bg-black/50 hover:bg-[#C4A66A] text-white flex items-center justify-center transition-colors" aria-label="Previous" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => onMove(1)} className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 bg-black/50 hover:bg-[#C4A66A] text-white flex items-center justify-center transition-colors" aria-label="Next" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
            <button onClick={onClose} className="absolute right-3 top-3 h-10 w-10 bg-black/50 hover:bg-[#C4A66A] text-white flex items-center justify-center" aria-label="Close" data-testid="gallery-close"><X className="h-5 w-5" /></button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function GalleryGrid({ items, limit }) {
  const [idx, setIdx] = useState(-1);
  const shown = limit ? items.slice(0, limit) : items;
  const move = (d) => setIdx((i) => (i + d + shown.length) % shown.length);
  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5" data-testid="gallery-grid">
        {shown.map((g, i) => (
          <button key={g.id} onClick={() => setIdx(i)} className="group relative block w-full overflow-hidden break-inside-avoid bg-slate-200" data-testid={`gallery-item-${g.id}`}>
            {isVideo(g.photo)
              ? <video src={`${mediaUrl(g.photo)}#t=0.5`} muted playsInline preload="metadata" className="w-full h-auto aspect-video object-cover bg-[#0C1726] transition-transform duration-700 group-hover:scale-[1.04]" />
              : <img src={mediaUrl(g.photo)} alt={g.caption || g.category} loading="lazy" className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.04]" />}
            {isVideo(g.photo) && <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="h-14 w-14 rounded-full bg-[#0C1726]/70 border border-[#C4A66A] flex items-center justify-center group-hover:scale-110 transition-transform"><Play className="h-5 w-5 text-[#C4A66A] ml-0.5" fill="currentColor" /></span></span>}
            <span className="absolute inset-0 bg-gradient-to-t from-[#0C1726]/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="absolute left-4 right-4 bottom-4 text-left text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <span className="block text-[10px] uppercase tracking-[0.25em] text-[#C4A66A]">{g.category}</span>
              {g.caption && <span className="block font-serif text-lg leading-snug mt-1">{g.caption}</span>}
            </span>
          </button>
        ))}
      </div>
      <Lightbox items={shown} index={idx} onClose={() => setIdx(-1)} onMove={move} />
    </>
  );
}

export function GalleryPreview() {
  const items = useGallery();
  if (!items?.length) return null;
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="home-gallery">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <div className="site-eyebrow">Life at VCAJ</div>
            <h2 className="font-serif text-3xl lg:text-5xl font-semibold text-[#14263D] mt-4">Our people, our moments.</h2>
          </div>
          <Link to="/life-at-vcaj" className="text-xs uppercase tracking-[0.25em] text-[#14263D] site-link inline-flex items-center gap-2" data-testid="home-gallery-all">See Life at VCAJ <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        <GalleryGrid items={items} limit={6} />
      </div>
    </section>
  );
}

export default function Gallery() {
  useSeo("Life at VCAJ — Office & Celebrations", "Photos and videos of the VCAJ & Associates LLP office, team celebrations and events.");
  const items = useGallery();
  const [cat, setCat] = useState("All");
  const shown = useMemo(() => (items || []).filter((g) => cat === "All" || g.category === cat), [items, cat]);
  return (
    <div data-testid="gallery-page">
      <PageHero testid="gallery-hero" image={IMAGES.team} eyebrow="Life at VCAJ" title="Our people, our moments."
        intro="Our office, our people and the moments we celebrate together." />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`px-4 h-9 text-xs font-semibold tracking-wide border transition-colors ${cat === c ? "bg-[#14263D] text-white border-[#14263D]" : "bg-white border-slate-300 text-slate-600 hover:border-[#14263D]"}`} data-testid={`gallery-filter-${c.toLowerCase()}`}>{c}</button>
            ))}
          </div>
          {items === null && <div className="text-sm text-slate-500">Loading…</div>}
          {items !== null && shown.length === 0 && (
            <div className="py-20 text-center border border-dashed border-slate-300 bg-white" data-testid="gallery-empty">
              <ImageIcon className="h-8 w-8 text-[#C4A66A] mx-auto" strokeWidth={1.5} />
              <p className="font-serif text-2xl text-[#14263D] mt-4">Photos and videos coming soon.</p>
            </div>
          )}
          {shown.length > 0 && <GalleryGrid key={cat} items={shown} />}
        </div>
      </section>
    </div>
  );
}
